import React, { useMemo } from 'react';
import { getISODay } from 'date-fns';
import { daysDifference, parseIsoTzLess as parse } from '../../date.utils';
import Tooltip from '@mui/material/Tooltip';
import * as css from './pill.module.scss';
import Icon, { IconType } from '../icon';
import { styled } from '@mui/system';

const PillTooltip = styled(Tooltip)({
  tooltip: {
    color: '#000',
    backgroundColor: '#f4f6f7',
    border: '1px solid #abb1bd',
    fontSize: '12px',
    fontFamily: 'Barlow',
  },
});

interface PillActivityInfo {
  id: string;
  startDato: string;
  sluttDato: string;
  behov: number;
}

interface BasePillProps {
  tooltipText: string;
  widthClass?: string;
  widthValue?: number;
  leftOffset?: number;
  colorPatternClass: string;
  extraClasses?: string[];
  pillIdentifier: string | Function;
  onClick?: Function;
  onHover?: Function;
  onMouseIn?: Function;
  onMouseOut?: Function;
}

const BasePill: React.FC<React.PropsWithChildren<BasePillProps>> = ({
  tooltipText,
  widthClass,
  widthValue,
  leftOffset,
  colorPatternClass,
  extraClasses,
  children,
  pillIdentifier,
  onClick = () => {},
  onHover = () => {},
  onMouseIn = () => {},
  onMouseOut = () => {},
}) => {
  const className = [
    'pill',
    'practice-period-color-scheme',
    colorPatternClass,
    widthClass || '',
    extraClasses ? extraClasses.join(' ') : '',
    onClick ? 'clickable' : '',
  ].join(' ');

  const style = Object.assign(
    widthValue ? { width: `${widthValue}px` } : {},
    leftOffset ? { left: `${leftOffset}px` } : {},
  );

  return (
    <PillTooltip arrow title={tooltipText} disableInteractive>
      <div
        className={className}
        onClick={() => onClick(pillIdentifier)}
        onMouseOver={() => onHover(pillIdentifier)}
        onMouseEnter={() => onMouseIn(pillIdentifier)}
        onMouseLeave={() => onMouseOut(pillIdentifier)}
        style={style}
      >
        {children}
      </div>
    </PillTooltip>
  );
};

interface PracticePeriodPillProps {
  colorPatternClass: string;
  extraClasses?: string[];
  practicePeriod: PillActivityInfo;
  pxPerDayScale: number;
  isWeekly: boolean;
  earliestVisibleIsoDate: string;
  tooltipText: string;
  onClick?: Function;
  onHover?: Function;
  onMouseIn?: Function;
  onMouseOut?: Function;
  latestVisibleIsoDate: string;
  warningIcon?: boolean;
}

const PracticePeriodPill: React.FC<
  React.PropsWithChildren<PracticePeriodPillProps>
> = ({
  colorPatternClass,
  extraClasses,
  practicePeriod,
  pxPerDayScale,
  isWeekly,
  earliestVisibleIsoDate,
  tooltipText,
  onClick = () => {},
  onHover = () => {},
  onMouseIn = () => {},
  onMouseOut = () => {},
  latestVisibleIsoDate,
  warningIcon,
}) => {
  const widthFromDates = useMemo(
    () =>
      daysDifference(
        practicePeriod.startDato > earliestVisibleIsoDate
          ? practicePeriod.startDato
          : earliestVisibleIsoDate,
        practicePeriod.sluttDato < latestVisibleIsoDate
          ? practicePeriod.sluttDato
          : latestVisibleIsoDate,
      ) *
        pxPerDayScale -
      2,
    [
      practicePeriod,
      earliestVisibleIsoDate,
      latestVisibleIsoDate,
      pxPerDayScale,
    ],
  );

  const leftOffsetFromDates = useMemo(() => {
    const crossesLeftEdge = practicePeriod.startDato < earliestVisibleIsoDate;
    if (!isWeekly || crossesLeftEdge) {
      return undefined;
    }
    const dayOfWeek = getISODay(parse(practicePeriod.startDato));
    return pxPerDayScale * (dayOfWeek - 1);
  }, [practicePeriod, earliestVisibleIsoDate, isWeekly, pxPerDayScale]);

  const showWarningIcon = useMemo(
    /* When the pill width is too small, the icon will overflow and fully or partly disappear.
     * Thus it will not be rendered at all when width <= 45
     * */
    () => warningIcon && widthFromDates > 45,
    [warningIcon, widthFromDates],
  );

  return (
    <BasePill
      tooltipText={tooltipText}
      colorPatternClass={colorPatternClass}
      onClick={onClick}
      onHover={onHover}
      onMouseIn={onMouseIn}
      onMouseOut={onMouseOut}
      extraClasses={extraClasses}
      pillIdentifier={'pill-' + practicePeriod.id}
      widthValue={widthFromDates}
      leftOffset={leftOffsetFromDates}
    >
      <div className={css.pillContent}>
        {practicePeriod.behov}
        {showWarningIcon && (
          <span className={css.iconContainer}>
            <Icon type={IconType.Warning} />
          </span>
        )}
      </div>
    </BasePill>
  );
};

export default PracticePeriodPill;
